import usePlatformAccounts from './usePlatforms';
import type { AuthenticatedUser } from '../store/auth-store';
import {
  hasCompletedPlatformOnboarding,
  hasDismissedPlatformActivation,
} from '../utils/onboarding';

export const countConnectedPlatformAccounts = (
  data?: { platforms?: Record<string, unknown[]> | null } | null,
): number =>
  Object.values(data?.platforms ?? {}).reduce((total, accounts) => {
    if (!Array.isArray(accounts)) {
      return total;
    }
    return total + accounts.length;
  }, 0);

const getFirstConnectedPlatform = (
  data?: { platforms?: Record<string, unknown[]> | null } | null,
): string | null => {
  const platforms = data?.platforms ?? {};
  for (const [platform, accounts] of Object.entries(platforms)) {
    if (Array.isArray(accounts) && accounts.length > 0) {
      return platform;
    }
  }
  return null;
};

const usePlatformActivation = (user?: AuthenticatedUser | null) => {
  const userId = user?.id ?? null;
  const platformAccounts = usePlatformAccounts({ enabled: Boolean(userId), userId });
  const connectedAccountCount = countConnectedPlatformAccounts(platformAccounts.data);
  const firstConnectedPlatform = getFirstConnectedPlatform(platformAccounts.data);
  const hasConnectedPlatforms = connectedAccountCount > 0;
  const hasDismissed = hasDismissedPlatformActivation(userId);
  const hasCompletedOnboarding = hasCompletedPlatformOnboarding(userId);
  const isKnownWorkspace = hasDismissed || hasCompletedOnboarding;
  const canEvaluate = Boolean(userId) && !platformAccounts.isLoading && !platformAccounts.isError;

  return {
    connectedAccountCount,
    firstConnectedPlatform,
    hasConnectedPlatforms,
    hasDismissed,
    hasCompletedOnboarding,
    isKnownWorkspace,
    isLoading: Boolean(userId) && platformAccounts.isLoading,
    isError: platformAccounts.isError,
    shouldShowInterstitial: canEvaluate && !hasConnectedPlatforms && !isKnownWorkspace,
  };
};

export default usePlatformActivation;
